import React, { useEffect, useState } from 'react';
import { FlatList, Image, Modal } from "react-native"
import type { AthleteProps, BracketCompetitionProps, BracketProps, CompetitionPlayerBracketProps, LeagueProps, PlayerBracketProps, TeamProps } from '../../types';
import moment from 'moment-mini';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import { BracketApi, BracketComeptitionApi, BracketCompetitionHelpers } from '../api';

type PlayerBracketSelectorProps = {
    player_id?:string,
    visible?:boolean,
    view_mode?:'desktop'|'mobile',
    bracket_id?:string,
    leagues:LeagueProps[],
    insets?:{ top:number, bottom:number, left:number, right:number },
    onCreateNew?:(pb:PlayerBracketProps) => void,
    onClose:() => void,
    onSelect:(pb:PlayerBracketProps) => void,
}

const PlayerBracketSelector = ({ visible, view_mode, insets, bracket_id, leagues, onSelect, onClose, onCreateNew }:PlayerBracketSelectorProps) => {
    const Colors = useColors();
    const [ action_loading, setActionLoading ] = useState(false);
    const [ my_brackets_data, setMyBracketsData ] = useState<{
        loading:boolean,
        bracket?:BracketProps,
        player_brackets:PlayerBracketProps[],
        competition_player_brackets:CompetitionPlayerBracketProps[],
        champion_teams:TeamProps[],
        champion_athletes:AthleteProps[]
        my_competitions:BracketCompetitionProps[]
    }>({
        loading:false,
        player_brackets:[],
        competition_player_brackets:[],
        champion_athletes:[],
        champion_teams:[],
        my_competitions:[]
    });
    const { loading, bracket, player_brackets, champion_athletes, champion_teams } = my_brackets_data;

    useEffect(() => {
        if(!visible || !bracket_id){ return }
        getDataFromServer(bracket_id);
    },[visible, bracket_id])

    const getDataFromServer = async(bracket_id:string) => {
        setMyBracketsData({ ...my_brackets_data, loading: true });
        const bk_resp = await BracketApi.getBracketById(bracket_id);
        const my_response = await BracketComeptitionApi.getMyPlayerBrackets(bracket_id);
        let champ_team_ids = my_response.player_brackets.filter(pb => pb.champion_id_type == 'team').map(b => b.champion_id ?? '')
        const champ_teams = await BracketApi.getTeamsByIds(champ_team_ids);
        let champ_athlete_ids = my_response.player_brackets.filter(pb => pb.champion_id_type == 'athlete').map(b => b.champion_id ?? '')
        const champ_athletes = await BracketApi.getAthletesByIds(champ_athlete_ids);
        if(!my_response){ return onClose() }
        setMyBracketsData({
            ...my_brackets_data,
            loading:false,
            bracket: bk_resp?.bracket,
            my_competitions: my_response.bracket_competitions,
            player_brackets: my_response.player_brackets,
            champion_athletes: champ_athletes,
            champion_teams: champ_teams,
            competition_player_brackets: my_response.competition_player_brackets
        })
    }

    const handleCreateNew = async() => {
        if(!bracket_id || !onCreateNew){ return }
        if(action_loading){ return }
        setActionLoading(true);
        let new_pb = BracketCompetitionHelpers.createEmptyPlayerBracket('', bracket_id);
        new_pb = await BracketComeptitionApi.createPlayerBracket(new_pb);
        onCreateNew(new_pb);
        setActionLoading(false);
    }

    const renderPlayerBrackets = (data : { item:PlayerBracketProps, index:number }) => {
        const champion_team = data.item.champion_id_type == 'team' ? champion_teams.find(t => t.team_id == data.item.champion_id) : undefined
        const champion_athlete = data.item.champion_id_type == 'athlete' ? champion_athletes.find(t => t.team_id == data.item.champion_id) : undefined
        return (
            <Button transparent style={{ padding:10, flexDirection:'row', alignItems:'center', borderRadius:0, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => onSelect(data.item)}>
                {champion_team ?
                <View transparent style={{ marginRight:5 }}>
                    <Image
                        source={{ uri: champion_team.image?.url }}
                        style={{ height:35, width:35, borderRadius:4 }}
                        resizeMode='cover'
                    />
                </View>
                :champion_athlete ?
                <View transparent style={{ marginRight:5 }}>
                    <Image
                        source={{ uri: champion_athlete.image?.url }}
                        style={{ height:35, width:35, borderRadius:4 }}
                        resizeMode='cover'
                    />
                </View>
                :<></>}
                <View transparent style={{ flex:1 }}>
                    <Text size={14} theme='h1' weight='bold'>{data.item.bracket_name}</Text>
                    <Text style={{ marginTop:4 }} size={12} theme='light' weight='regular'>{(data.item.completion_pct*100).toFixed()}% Complete</Text>
                </View>
                <Button
                    title='VIEW'
                    type='action'
                    style={{ padding:10 }}
                    disabled
                    onPress={() => console.log('')}
                />
            </Button>
        )
    }

    const renderBrackets = (data: { item:string, index:number }) => {
        if(!bracket){ return <></> }
        const league= leagues.find(l => l.league_id == bracket.league_id);
        if(!league){ return <></> }
        const b_pbs = player_brackets.filter(pb => pb.bracket_id == data.item);
        const started = moment().isAfter(moment(bracket.scheduled_datetime)) ? true : false
        return (
            <View transparent>
                <View type='header' style={{ padding:10, flexDirection:'row', alignItems:'center' }}>
                    <View transparent>
                        <Image
                            source={{ uri: league.league_image }}
                            style={{ height:35, width:35, borderRadius:4 }}
                        />
                    </View>
                    <View transparent style={{ flex:1 }}>
                        <Text size={14} theme='h1' weight='bold'>{bracket.bracket_name}</Text>
                        <Text style={{ marginTop:4 }} size={12} theme='light' weight='bold'>{started?'In Progress':`Starts at ${moment(bracket.scheduled_datetime).format('MMM DD YYYY hh:mm a')}`}</Text>
                    </View>
                </View>
                <View transparent style={{ padding:10 }}>
                    <FlatList
                        data={b_pbs}
                        renderItem={renderPlayerBrackets}
                        keyExtractor={(item) => item.player_bracket_id.toString()}
                    />
                </View>
            </View>
        )
    }


    return (
        <Modal
            visible={visible}
            style={{ flex:1 }}
            transparent
            animationType={view_mode == 'desktop' ? 'fade' : 'slide'}
            >
            <View type='blur' style={view_mode == 'desktop' ? { flex:1, justifyContent:'center', alignItems:'center' } : { flex:1, justifyContent:'flex-end', paddingBottom:(insets?.bottom ?? 0) }}>
                <View float style={view_mode == 'desktop' ? { width:500 }:{ margin:10, maxHeight:650 }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h1'>My Brackets</Text>
                        </View>
                        {bracket_id && onCreateNew ?
                        <Button 
                            type='success' 
                            disabled={action_loading}
                            loading={action_loading}
                            style={{ flexDirection:'row', alignItems:'center', padding:10, opacity: action_loading?0.5:1 }} 
                            onPress={() => handleCreateNew()}
                            >
                            <Text size={14} color={Colors.text.white}>NEW</Text>
                        </Button>
                        :<></>}
                    </View>
                    <View transparent style={{ flexGrow:1, marginTop:1 }}>
                        {bracket_id ?
                        <FlatList
                            refreshing={loading}
                            data={[bracket_id]}
                            renderItem={renderBrackets}
                            keyExtractor={(item) => item.toString()}
                        />
                        :<></>}
                    </View>
                    <View type='footer' style={{ flexDirection:'row', padding:10 }}>
                        <Button
                            type='close'
                            title='CLOSE'
                            style={{ flex:1 }}
                            onPress={() => onClose()}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    )
}

export default PlayerBracketSelector